import React, { useEffect } from 'react';
import { PaymentStatusDataForm } from '../../../../app/models/PaymentStatus';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import { momentParseDate } from '../../../../helpers';

type Props = {
    paymentStatus: PaymentStatusDataForm;
    setPaymentStatus: (PaymentStatus: PaymentStatusDataForm) => void;
    errorFields?: any;
    onCalculateExchangeRate?: () => void;
    fetchingCalculateExchangeRate?: boolean;
};

const PaymentStatusForm = ({ paymentStatus, setPaymentStatus, errorFields, onCalculateExchangeRate, fetchingCalculateExchangeRate }: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setPaymentStatus({ ...paymentStatus, [name]: value });
    };

    return (
        <>
            <div className="form-group mt-2">
                <label htmlFor="contactEmail">Sociedad</label>
                <input
                    type="society"
                    className="form-control"
                    id="society"
                    name="society"
                    value={paymentStatus.society}
                    onChange={handleChange}
                    onFocus={() => onFocusRemove('society')}
                    disabled
                />
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                    {fieldErrorMessage(`society`)}
                </div>
            </div> 
         
            <div className="form-group mt-2">
                <label htmlFor="client">Cliente</label>
                <input
                    type="text"
                    className="form-control"
                    id="client"
                    name="client"
                    value={paymentStatus.client}
                    onChange={handleChange}
                    disabled

                />
            </div>
            
            <div className="form-group mt-2">
                <label htmlFor="contactEmail">Email de Contacto</label>
                <input
                    type="email"
                    className="form-control"
                    id="contact_email"
                    name="contact_email"
                    value={paymentStatus.contact_email}
                    onChange={handleChange}
                    onFocus={() => onFocusRemove('contact_email')}
                />
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                    {fieldErrorMessage(`contact_email`)}
                </div>
            </div>
            <div className="form-group mt-2">
                <label htmlFor="contactPhone">Teléfono de Contacto</label>
                <input
                    className="form-control"
                    name="contact_phone"
                    value={paymentStatus.contact_phone}
                    onChange={handleChange}
                    disabled
                />
            </div>
            <div className="form-group mt-2">
                <label htmlFor="paymentConditionId">Condición de Pago</label>
                <input
                    className="form-control"
                    name="payment_condition"
                    value={paymentStatus.payment_condition}
                    onChange={handleChange}
                    disabled
                />
            </div>
            <div className="form-group mt-2">
                <label htmlFor="currency">Moneda</label>
                <input
                    className="form-control"
                    name="currency"
                    value={paymentStatus.currency?.name || ''}
                    onChange={handleChange}
                    disabled
                />
            </div>
            <div className="form-group mt-2">
                <label htmlFor="createdAt">Fecha de Emisión</label>
                <input
                    type="date"
                    className="form-control"
                    id="issue_date"
                    name="issue_date"
                    value={paymentStatus.issue_date}
                    onChange={handleChange}
                    onFocus={() => onFocusRemove('issue_date')}
                />
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                    {fieldErrorMessage(`issue_date`)}
                </div>
            </div>

            {/* Campos de Tipo de Cambio */}
            <div className="row">
                <div className="col-md-6">
                    <div className="form-group mt-2">
                        <label htmlFor="preliminary_exchange_rate_date">Fecha Cambio Provisional:</label>
                        <input
                            type="text"
                            className="form-control"
                            id="preliminary_exchange_rate_date"
                            value={momentParseDate(paymentStatus.preliminary_exchange_rate_date)}
                            disabled
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group mt-2">
                        <label htmlFor="final_exchange_rate_date">Fecha Cambio Final:</label>
                        <input
                            type="text"
                            className="form-control"
                            id="final_exchange_rate_date"
                            value={momentParseDate(paymentStatus.final_exchange_rate_date)}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-6">
                    <div className="form-group mt-2">
                        <label htmlFor="prelim_rate">Valor Cambio Provisional:</label>
                        <input
                            type="text"
                            className="form-control"
                            id="prelim_rate"
                            value={paymentStatus.prelim_rate || ''}
                            disabled
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group mt-2">
                        <label htmlFor="final_rate">Valor Cambio Final:</label>
                        <input
                            type="text"
                            className="form-control"
                            id="final_rate"
                            value={paymentStatus.final_rate || ''}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="form-group mt-2">
                <label htmlFor="local_currency_amount">Valor Moneda Local (CLP)</label>
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control"
                        id="local_currency_amount"
                        name="local_currency_amount"
                        value={paymentStatus.local_currency_amount != null ? paymentStatus.local_currency_amount : ''}
                        readOnly
                        placeholder="Use el botón Calcular"
                    />
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title="Actualiza tipos de cambio desde la oferta y calcula el valor en CLP"
                        disabled={fetchingCalculateExchangeRate}
                        onClick={() => {
                            if (onCalculateExchangeRate) {
                                onCalculateExchangeRate();
                            }
                        }}
                    >
                        {fetchingCalculateExchangeRate
                            ? <><span className="spinner-border spinner-border-sm me-1" /> Calculando...</>
                            : <><i className="mdi mdi-calculator" /> Calcular</>
                        }
                    </button>
                </div>
                <small className="text-muted">
                    Actualiza fechas y tasas desde la oferta. Usa tasa {Number(paymentStatus.final_rate ?? 0) > 0 ? 'final' : 'provisional'}{' '}
                    para calcular CLP.
                </small>
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                    {fieldErrorMessage('local_currency_amount')}
                </div>
            </div>
        </>
    );
};

export default PaymentStatusForm;
